import React from 'react';
import { capturePortalError } from '../lib/portalErrorReporting';
import { readableErrorMessage } from '../utils/appErrors';

type ErrorBannerProps = {
  message: string;
  onRetry?: () => void;
  className?: string;
  action?: React.ReactNode;
  /** When set, this unexpected failure is logged automatically without exposing context values. */
  reportContext?: Record<string, unknown>;
};

const ErrorBanner: React.FC<ErrorBannerProps> = ({ message, onRetry, className, action, reportContext }) => {
  const classes = className ? `alert alert-warn ${className}` : 'alert alert-warn';
  const loggedFailureRef = React.useRef('');

  React.useEffect(() => {
    if (reportContext == null) return;
    const failureKey = `${message}|${Object.keys(reportContext).sort().join(',')}`;
    if (loggedFailureRef.current === failureKey) return;
    loggedFailureRef.current = failureKey;
    void capturePortalError({
      source: 'workflow',
      error: new Error(message),
      operation: 'error_banner',
      context: {
        context_keys: Object.keys(reportContext).sort().join(','),
      },
    });
  }, [message, reportContext]);

  return (
    <div className={classes} role="alert">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <span>{readableErrorMessage(message)}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {action}
          {onRetry ? (
            <button type="button" className="btn xs subtle" onClick={onRetry}>
              Retry
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ErrorBanner;
